import React from 'react';
import { useTranslation } from 'react-i18next';
import CurrencyInput from '../forms/CurrencyInput';

type MemberEntry = {
  id: number;
  label: string;
};

type SplitMembersListProps = {
  members: MemberEntry[];
  memberValues: Record<number, number>;
  memberRations?: Record<number, number | undefined>;
  onSetValue: (memberId: number, value: number) => void;
  onSetRations: (memberId: number, rations: number | undefined) => void;
  headerTitle: string;
  onSplit: () => void;
  onClear: () => void;
  isResetable: boolean;
  remainingAmount: number;
  remainingLabel: string;
};

export default function SplitMembersList({
  members,
  memberValues,
  memberRations,
  onSetValue,
  onSetRations,
  headerTitle,
  onSplit,
  onClear,
  isResetable,
  remainingAmount,
  remainingLabel,
}: SplitMembersListProps) {
  const { t } = useTranslation('', { keyPrefix: 'page.financial.splitModal' });
  return (
    <>
      <div className="my-2 flex items-center justify-between">
        <div className="flex items-center space-x-3 pl-2">
          <h4 className="font-medium">{headerTitle}</h4>
        </div>
        <div className="flex items-center gap-4">
          <button
            type="button"
            className="text-primary-700 text-sm font-semibold underline"
            onClick={() => (isResetable ? onClear() : onSplit())}>
            {isResetable ? t('clear') : t('split')}
          </button>
        </div>
      </div>

      <div className="max-h-60 space-y-4 overflow-y-auto p-2">
        {members.map((member) => (
          <div
            key={`${member.id}_container_amount_`}
            className="flex items-center justify-between border-b border-secondary-200 pb-3">
            <div className="flex items-center space-x-3">
              <span className="flex-1 cursor-default text-sm font-medium">{member.label}</span>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                className="text-primary-700 p-2 hover:text-primary-900"
                onClick={() => {
                  const current = memberRations?.[member.id] ?? 0;
                  const next = Math.max(0, (current || 0) - 1);
                  onSetRations(member.id, next);
                }}>
                −
              </button>
              <button
                type="button"
                className="h-8 w-8 items-center justify-center rounded-lg bg-secondary-200 p-2 text-primary-900"
                onClick={() => {
                  const current = memberRations?.[member.id];
                  const next = current === undefined ? 1 : current === 0 ? 1 : 0;
                  onSetRations(member.id, next);
                }}>
                <span className="flex h-full w-full items-center justify-center">
                  {memberRations?.[member.id] !== undefined
                    ? `${memberRations?.[member.id]}x`
                    : '-'}
                </span>
              </button>
              <button
                type="button"
                className="text-primary-700 p-2 hover:text-primary-900"
                onClick={() => {
                  const current = memberRations?.[member.id] ?? 0;
                  const next = (current || 0) + 1;
                  onSetRations(member.id, next);
                }}>
                +
              </button>

              <CurrencyInput
                type="text"
                className="w-20"
                handleChange={(value) => onSetValue(member.id, value)}
                value={(() => {
                  const v = memberValues[member.id];
                  const hasValue = v !== undefined && Math.abs(v) > 0;
                  return hasValue ? `€ ${(Math.abs(v) / 100).toFixed(2)}` : '';
                })()}
                debounced
              />
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 flex items-center justify-between rounded-md bg-secondary-200 bg-opacity-50 p-3">
        <span className="font-medium">{remainingLabel}:</span>
        <span className={`font-medium ${remainingAmount === 0 ? 'text-success' : 'text-danger'}`}>
          {Number.isNaN(remainingAmount) ? '?' : `€${(remainingAmount / 100).toFixed(2)}`}
        </span>
      </div>
    </>
  );
}
